"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface Member {
  user: {
    username: string | null;
    displayName: string | null;
    avatar: string | null;
  };
}

interface CollaboratorAvatarsProps {
  memberCount: number;
  members?: Member[];
  maxVisible?: number;
}

export function CollaboratorAvatars({
  memberCount,
  members = [],
  maxVisible = 3,
}: CollaboratorAvatarsProps) {
  if (memberCount === 0) return null;

  const visibleMembers = members.slice(0, maxVisible);
  const extraCount = memberCount - visibleMembers.length;

  const getInitials = (name: string | null, username: string | null) => {
    const display = name || username || "?";
    return display
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  return (
    <div className="flex items-center">
      <div className="flex -space-x-3 isolate">
        {visibleMembers.map((member, i) => (
          <Avatar 
            key={member.user.username || i}
            className="h-7 w-7 border-[2.5px] border-[#0c0c0e] ring-1 ring-white/5 hover:translate-y-[-2px] hover:z-10 transition-all duration-300 cursor-default shadow-lg"
            title={member.user.displayName || member.user.username || "Collaborator"}
          >
            <AvatarImage src={member.user.avatar || undefined} />
            <AvatarFallback className="text-[10px] font-black bg-zinc-800 text-zinc-400">
              {getInitials(member.user.displayName, member.user.username)}
            </AvatarFallback>
          </Avatar>
        ))}
        
        {extraCount > 0 && (
          <div 
            className="relative h-7 w-7 rounded-full bg-zinc-900 border-[2.5px] border-[#0c0c0e] ring-1 ring-white/5 flex items-center justify-center text-[10px] font-black text-zinc-500 shadow-lg z-0"
            title={`${extraCount} more collaborators`}
          >
            +{extraCount}
          </div>
        )}
      </div>
      
      <span className="ml-3 text-[11px] font-bold text-zinc-500 tracking-tight">
        {memberCount === 1 ? "1 collaborator" : `${memberCount} collaborators`}
      </span>
    </div>
  );
}

